import { useCallback } from "react";

import type { MultiSelectFieldValue as MultiSelectCheckboxFieldValue } from "../../types";

/**
 * MultiSelectフィールドの値を MultiSelectFieldValue に正規化する
 */
export const normalizeMultiSelectValue = (
  value: unknown,
): MultiSelectCheckboxFieldValue => {
  if (Array.isArray(value)) {
    return value.map(String);
  }

  if (typeof value === "string") {
    return parseStringToArray(value);
  }

  return [];
};

/**
 * 文字列を配列にパースする関数
 */
const parseStringToArray = (value: string): MultiSelectCheckboxFieldValue => {
  if (!value || value === "[]") {
    return [];
  }

  // JSON形式の場合
  if (value.startsWith("[") && value.endsWith("]")) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      console.warn(`Invalid JSON format for multi-select: ${value}`);
      return [];
    }
  }

  // JSON形式でない通常の文字列の場合は、単一要素の配列として扱う
  return [value];
};

/**
 * 選択値の追加/削除を行う関数
 */
const toggleMultiSelectValue = (
  currentValues: MultiSelectCheckboxFieldValue,
  targetValue: string,
  isSelected: boolean,
): string[] => {
  if (isSelected) {
    return currentValues.includes(targetValue)
      ? currentValues
      : [...currentValues, targetValue];
  } else {
    return currentValues.filter((value) => value !== targetValue);
  }
};

/**
 * 選択状態を確認する関数
 */
const isValueSelected = (
  values: MultiSelectCheckboxFieldValue,
  targetValue: string,
): boolean => {
  return values.includes(targetValue);
};

interface UseMultiSelectFieldParams {
  value: unknown;
  onChange: (value: MultiSelectCheckboxFieldValue) => void;
}

interface UseMultiSelectFieldResult {
  selectedValues: MultiSelectCheckboxFieldValue;
  handleCheckboxChange: (optionValue: string, checked: boolean) => void;
  isValueSelected: (optionValue: string) => boolean;
}

/**
 * MultiSelectフィールドの状態管理とイベントハンドリングを提供するカスタムフック
 */
export const useMultiSelectField = ({
  value,
  onChange,
}: UseMultiSelectFieldParams): UseMultiSelectFieldResult => {
  // 入力値を正規化
  const selectedValues = normalizeMultiSelectValue(value);

  // チェックボックスの変更ハンドラー
  const handleCheckboxChange = useCallback(
    (optionValue: string, checked: boolean) => {
      const newValues = toggleMultiSelectValue(
        selectedValues,
        optionValue,
        checked,
      );
      onChange(newValues);
    },
    [selectedValues, onChange],
  );

  // 値の選択状態を確認する関数
  const checkIsValueSelected = useCallback(
    (optionValue: string) => isValueSelected(selectedValues, optionValue),
    [selectedValues],
  );

  return {
    selectedValues,
    handleCheckboxChange,
    isValueSelected: checkIsValueSelected,
  };
};
